// @flow

import React, { useEffect } from "react";

import { RadioLabel, CustomRadio } from "../styles";
import Accordion from "src/components/Accordion.v2";
import AccordionContent from "../AccordionContent";
import { Checkbox, TextInput } from "src/components/Unifize";
import Radio from "src/components/Radio";
import { checkIfRequestingAllowed } from "src/utils/checklist";

import type { ApprovalFieldSettingsV2 } from "src/types";

type Props = {
  settings: ApprovalFieldSettingsV2,
  saveSettings: Function,
};

const BasicSettings = ({ settings, saveSettings }: Props) => {
  const isRequestingAllowed = checkIfRequestingAllowed(settings);
  const isAdhocSelected = settings?.requiredApprovers === "adhoc";

  useEffect(() => {
    // Set to true automatically when ad-hoc is selected
    if (isAdhocSelected && settings?.showRequestApprovalButton !== true) {
      saveSettings(["showRequestApprovalButton"], true);
      return;
    }

    // Set to false automatically when no approvers configured and ad-hoc isn't selected
    if (
      !isAdhocSelected &&
      !isRequestingAllowed &&
      settings?.showRequestApprovalButton !== false
    ) {
      saveSettings(["showRequestApprovalButton"], false);
    }
  }, [
    isAdhocSelected,
    isRequestingAllowed,
    settings?.showRequestApprovalButton,
    saveSettings,
  ]);

  const toggleAdHoc = (value) => {
    if (value) {
      saveSettings(["showRequestApprovalButton"], true);
    }
    saveSettings(["requiredApprovers"], "adhoc");
  };

  return (
    <Accordion
      title="Basic approval settings"
      data-cy="basic-approval-settings-accordion"
    >
      <AccordionContent sx={{ gap: 2 }}>
        <CustomRadio data-cy="required-approvers-some-container">
          <Radio
            id="some"
            value="some"
            checked={settings?.requiredApprovers === "some"}
            handleChange={() => saveSettings(["requiredApprovers"], "some")}
            data-cy="required-approvers-some-radio"
          >
            <RadioLabel>Get signature of any</RadioLabel>
          </Radio>
          <TextInput
            type="number"
            placeholder="eg. 2"
            value={settings?.minApprovers}
            onChange={(e) => {
              if (Number(e.target?.value) < 1) {
                e.target.value = 1;
              }
              saveSettings(["minApprovers"], Number(e.target?.value ?? 1));
            }}
            min={1}
            data-cy="required-approvers-min-input"
          />

          <RadioLabel>user(s)</RadioLabel>
        </CustomRadio>

        <Radio
          id="adhoc"
          value="adhoc"
          checked={settings?.requiredApprovers === "adhoc"}
          handleChange={toggleAdHoc}
          data-cy="required-approvers-adhoc-radio"
        >
          <RadioLabel data-cy="required-approvers-adhoc-label">
            Select ad-hoc
          </RadioLabel>
        </Radio>

        <Checkbox
          checked={settings?.showRequestApprovalButton}
          isDisabled={
            settings?.requiredApprovers === "adhoc" || !isRequestingAllowed
          }
          label="Request approval button"
          onChange={(value) =>
            saveSettings(["showRequestApprovalButton"], value)
          }
          height="inherit"
          data-cy="request-approval-button-checkbox"
        />
      </AccordionContent>
    </Accordion>
  );
};

export default BasicSettings;
